import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, UserCog, MessageSquareReply, Landmark, ArrowLeftRight,
  FileBarChart2, Users, Wallet, Moon, Sun, Bell, Menu, X, ChevronRight,
  Store, Gift, ShieldCheck, ScrollText, Settings, UsersRound, BellRing,
} from "lucide-react";

const NAV = [
  {
    section: "YÖNETİM",
    items: [
      { to: "/", label: "Genel Bakış", icon: LayoutDashboard, end: true },
      { to: "/kullanicilar", label: "Kullanıcılar", icon: Users },
      { to: "/bayi-yonetimi", label: "Bayi Yönetimi", icon: Store },
      { to: "/hazir-cevaplar", label: "Hazır Cevaplar", icon: MessageSquareReply },
    ],
  },
  {
    section: "FİNANSAL İŞLEMLER",
    items: [
      { to: "/saha-hesaplari", label: "Saha Hesapları", icon: Landmark },
      { to: "/havale-fast", label: "Havale/FAST", icon: ArrowLeftRight, chevron: true },
      { to: "/saha-raporu", label: "Saha Raporu", icon: FileBarChart2 },
      { to: "/saha-ekipleri", label: "Saha Ekipleri", icon: UsersRound },
      { to: "/kasa-bilgisi", label: "Kasa Bilgisi", icon: Wallet },
    ],
  },
  {
    section: "SİTE YÖNETİMİ",
    items: [
      { to: "/bonuslar", label: "Bonuslar", icon: Gift },
      { to: "/bildirimler", label: "Bildirimler", icon: BellRing },
      { to: "/yetkiler", label: "Yetkiler & Roller", icon: ShieldCheck },
      { to: "/loglar", label: "Sistem Logları", icon: ScrollText },
      { to: "/kullanici-ayarlari", label: "Kullanıcı Ayarları", icon: UserCog },
      { to: "/site-ayarlari", label: "Site Ayarları", icon: Settings },
    ],
  },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-white ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden flex items-center justify-center shadow-sm">
        <img src="/brand-logo.jpg" alt="Sezar" className="w-full h-full object-contain" />
      </div>
      <span className="text-2xl font-extrabold tracking-tight">Sezar</span>
    </div>
  );
}

function SidebarContent({ onNavigate }) {
  return (
    <nav className="px-3 py-4 space-y-5" data-testid="sidebar-nav">
      {NAV.map((group, gi) => (
        <div key={gi} className="space-y-1">
          <div className="flex items-center gap-2 px-3 pt-1 pb-1">
            <span className="text-[11px] font-bold tracking-widest text-slate-400 dark:text-slate-500">{group.section}</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          </div>
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className={({ isActive }) =>
                  [
                    "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors duration-150",
                    isActive
                      ? "bg-gradient-to-r from-[#F0651E] to-[#F58A3E] text-white shadow-md shadow-orange-500/25"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={isActive ? "text-white" : "text-slate-500 dark:text-slate-400"} size={20} strokeWidth={2} />
                    <span className="flex-1">{item.label}</span>
                    {item.chevron && <ChevronRight size={16} className={isActive ? "text-white" : "text-slate-400"} />}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export default function Layout() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <div className="min-h-screen bg-[#f6f7fb] dark:bg-[#0f1117] text-slate-800 dark:text-slate-100">
      <header className="sticky top-0 z-40 h-16 flex items-center justify-between px-4 sm:px-6 bg-white/90 dark:bg-[#151824]/90 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <Brand />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => setDark((d) => !d)} className="p-2.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="theme-toggle" aria-label="Tema">
            {dark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button className="p-2.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 relative" data-testid="notifications-btn">
            <Bell size={20} />
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          <div className="relative ml-1">
            <div className="w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
              <img src="/brand-logo.jpg" alt="profil" className="w-full h-full object-cover" />
            </div>
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-[#151824]" />
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden lg:block w-[280px] shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151824] min-h-[calc(100vh-4rem)] sticky top-16 self-start overflow-y-auto max-h-[calc(100vh-4rem)]">
          <SidebarContent />
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-0 h-full w-[280px] bg-white dark:bg-[#151824] shadow-2xl overflow-y-auto">
              <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800">
                <Brand />
                <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X size={20} /></button>
              </div>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
